import { computed } from 'mobx'
import { merge, type Observable } from 'rxjs'
import type {
  DocumentRequestsViewModel,
  RequestedDocumentDescriptor,
} from '@/features/documents/DocumentRequestsViewModel'
import type {
  DocumentsAnswer,
  DocumentsQuestion,
} from '@/features/jobs/questions/questions'
import { truncateTextItems } from '@/utils/string-util'
import { QuestionAnswerEditorViewModelBase } from '@/features/jobs/questions/QuestionAnswerEditorViewModelBase'

/**
 * Question answer editor for `Documents` questions.
 */
export class DocumentsQuestionAnswerEditorViewModel extends QuestionAnswerEditorViewModelBase<DocumentsQuestion> {
  /** @inheritDoc */
  readonly type = 'Documents'

  constructor(readonly documentRequests: DocumentRequestsViewModel) {
    super()
  }

  /** @inheritDoc */
  override get answerChanged$(): Observable<unknown> {
    return merge(super.answerChanged$, this.documentRequests.changes$)
  }

  /**
   * Subset of document requests that were provided.
   */
  @computed get providedDocumentRequests() {
    return this.documentRequests.documentRequests.filter((d) => d.documentId)
  }

  /** @inheritDoc */
  @computed get isAnswered(): boolean {
    return this.providedDocumentRequests.length > 0
  }

  /** @inheritDoc */
  @computed get answer(): DocumentsAnswer {
    return {
      type: 'Documents',
      value: this.providedDocumentRequests.map((vm) => {
        const descriptor = vm.toDescriptor()
        return {
          id: descriptor.id,
          documentId: descriptor.documentId ?? '',
          supplemental: descriptor.supplemental,
          title: descriptor.title,
        }
      }),
    }
  }

  /** @inheritDoc */
  set(question: DocumentsQuestion) {
    const answers = question.answer?.value ?? []
    this.documentRequests.reconcile(
      question.options.map((option) => ({
        id: option.id,
        title: option.text,
        documentId: answers.find((a) => a.id === option.id)?.documentId,
        documentTagIds: option.documentTagIds,
        supplemental: false,
      })),
    )

    if (!question.answer) {
      return
    }

    const descriptors: RequestedDocumentDescriptor[] = []
    const answersById = new Map(answers.map((a) => [a.id, a]))
    for (const descriptor of this.documentRequests.documentRequestDescriptors) {
      descriptors.push({
        ...descriptor,
        documentId: answersById.get(descriptor.id)?.documentId,
      })
    }

    for (const answer of answers) {
      if (answer.supplemental) {
        descriptors.push({
          id: answer.id,
          title: answer.title || 'Additional file',
          documentId: answer.documentId,
          supplemental: answer.supplemental,
        })
      }
    }

    this.documentRequests.reconcile(descriptors)
  }

  /** @inheritDoc */
  protected formatTextAnswer(maxLength: number): string {
    return truncateTextItems(
      this.providedDocumentRequests.map((s) => s.title),
      maxLength,
    )
  }
}
